import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePerfume } from '../context/PerfumeContext';
import { useLanguage } from '../context/LanguageContext';
import { useHaptics } from '../hooks/useHaptics';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { RotateCcw, Save, Check, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export const ActionButtons = () => {
  const { clearBottle, saveCreation, formula, savedPerfumes, deletePerfume, loadPerfume } = usePerfume();
  const { t, language } = useLanguage();
  const { pour, success } = useHaptics();
  
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savedListOpen, setSavedListOpen] = useState(false);
  const [perfumeName, setPerfumeName] = useState('');
  
  const hasFormula = Object.keys(formula).length > 0;
  const hasSaved = savedPerfumes.length > 0;

  const handleClear = () => {
    pour();
    clearBottle();
  };

  const handleSave = () => {
    if (!perfumeName.trim()) return;
    
    saveCreation(perfumeName.trim());
    success();
    setSaveDialogOpen(false);
    setPerfumeName('');
    
    toast.success(t('savedSuccess'), {
      description: perfumeName,
    });
  };

  const handleLoad = (perfume) => {
    loadPerfume(perfume);
    setSavedListOpen(false);
    success();
  };

  const handleDelete = (e, perfumeId) => {
    e.stopPropagation();
    deletePerfume(perfumeId);
  };

  return (
    <>
      <motion.div 
        className="flex flex-col sm:flex-row gap-3 w-full max-w-md mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        {/* Start Over Button */}
        <Button
          variant="outline"
          onClick={handleClear}
          disabled={!hasFormula}
          className="flex-1 h-12 rounded-full border-foreground/20 hover:bg-foreground hover:text-background transition-all duration-300 disabled:opacity-40"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          {t('startOver')}
        </Button>

        {/* Save Button */}
        <Button
          onClick={() => setSaveDialogOpen(true)}
          disabled={!hasFormula}
          className="flex-1 h-12 rounded-full bg-foreground text-background hover:bg-foreground/90 transition-all duration-300 disabled:opacity-40"
        >
          <Save className="w-4 h-4 mr-2" />
          {t('saveCreation')}
        </Button>
      </motion.div>

      {/* Saved perfumes link */}
      {hasSaved && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          onClick={() => setSavedListOpen(true)}
          className="mt-4 text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
        >
          {t('savedPerfumes')} ({savedPerfumes.length})
        </motion.button>
      )}

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {t('saveTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('saveDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Input
              value={perfumeName}
              onChange={(e) => setPerfumeName(e.target.value)}
              placeholder={t('perfumeNamePlaceholder')}
              className="h-12 rounded-lg"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
              }}
            />
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setSaveDialogOpen(false)}
              className="rounded-full"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!perfumeName.trim()}
              className="rounded-full bg-foreground text-background hover:bg-foreground/90"
            >
              <Check className="w-4 h-4 mr-2" />
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Saved Perfumes Dialog */}
      <Dialog open={savedListOpen} onOpenChange={setSavedListOpen}>
        <DialogContent className="sm:max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {t('savedPerfumes')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
            <AnimatePresence>
              {savedPerfumes.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  {t('noSavedPerfumes')}
                </p>
              ) : (
                <div className="space-y-2">
                  {savedPerfumes.map((perfume) => (
                    <motion.div
                      key={perfume.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="flex items-center justify-between p-4 rounded-xl bg-secondary/30 hover:bg-secondary/50 cursor-pointer transition-colors group"
                      onClick={() => handleLoad(perfume)}
                    >
                      <div className="flex items-center gap-3">
                        {/* Color preview */}
                        <div 
                          className="w-8 h-8 rounded-full"
                          style={{ backgroundColor: perfume.mixedColor }}
                        />
                        
                        <div>
                          <p className="font-serif text-foreground">
                            {perfume.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {perfume.totalVolume} {t('ml')}
                          </p>
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleDelete(e, perfume.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </motion.div>
                  ))}
                </div>
              )}
            </AnimatePresence>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
